/**
 * Normaliza el objeto de error que emite pusher-js a `{ type, code, message }`.
 *
 * ## Por qué existe este archivo
 *
 * `DASHBOARD-ZC-KK` acumula 330 ocurrencias y 110 usuarios desde el 17-jun-2026,
 * y `DASHBOARD-ZC-KV` otras 87 con 36 usuarios. Los DOS reportan
 * `pusherError: { code: 'unknown' }`. Dos meses de una señal encendida que no
 * dice nada.
 *
 * No era que Pusher no mandara el código: es que lo leíamos en la ruta
 * equivocada. La instrumentación hacía `err.error.code`, y esa ruta no existe
 * en NINGUNA de las formas que emite pusher-js 7.6.0.
 *
 * ## Las formas reales (verificadas en el fuente vendorizado)
 *
 * `pusherVendorContract.test.js` lee el fuente de `node_modules` y falla si
 * alguna de estas cinco deja de emitirse tal cual. No confiamos en la memoria.
 *
 * 1. `connection.ts:106` — frame `pusher:error` del servidor:
 *      `{ type: 'PusherError', data: { code, message } }`
 *    → el código vive en **`data.code`**. Es la forma de KK.
 *
 * 2. `connection_manager.ts:305` — error de handshake:
 *      `{ type: 'WebSocketError', error: <getCloseError()> }`
 *    y `protocol.ts:139 getCloseError()` devuelve **otro**
 *      `{ type: 'PusherError', data: { code, message } }`
 *    → el código vive en **`error.data.code`**, anidado DOS niveles.
 *    Es la forma de KV, y es la razón de que KV también dijera 'unknown'.
 *
 * 3. `connection_manager.ts:160` — `{ type: 'HandshakeError', error }`, con el
 *    mismo anidado que (2).
 *
 * 4. `connection.ts:94` — `{ type: 'MessageParseError', error, data }`, donde
 *    `error` es una excepción nativa con `.message` y sin `.code`, y `data` es
 *    la TRAMA CRUDA del websocket (un string, no un objeto).
 *
 * 5. `transports/transport_connection.ts:168` — `socket.onerror` del navegador:
 *      `{ type: 'WebSocketError', error: <Event crudo> }`
 *    SEGUNDA variante de `WebSocketError`, estructuralmente distinta de la (2):
 *    aquí `.error` es el `Event` del navegador, **sin `.data` y sin `.code`**.
 *    Llega al `bind('error')` público vía `connection.ts:125` →
 *    `connection_manager.ts:272`.
 *    → **Para esta forma no hay código que extraer.** Degrada a 'unknown', que
 *    por la regla dura abre issue. Consecuencia para el rollout: `unknown` BAJA
 *    tras el despliegue, pero NO llega a cero. No es que el arreglo falle.
 *
 * Además, `connection.ts:154` emite el resultado de `getCloseError()` sin
 * envolver (idéntico a la forma 1), y `connection.ts:125` /
 * `connection_manager.ts:272` reemiten el error crudo del transporte (un
 * `CloseEvent` del navegador), que trae `.code`/`.reason` en la raíz y no tiene
 * `type`.
 *
 * ## La regla
 *
 * Una sola: desenvolver `.error` mientras exista, y en cada nivel preferir
 * `data.code` sobre `code` (pusher-js siempre mete el dato bueno en `data`).
 * Con eso las cinco formas y el evento crudo quedan cubiertos sin un `if` por
 * cada una, que es lo que se desincroniza cuando el vendor cambia.
 *
 * ## La excepción a la regla: el mensaje de una excepción NATIVA no se copia
 *
 * Hay un solo caso en que NO tomamos el `.message` de un nivel: cuando ese
 * nivel es una excepción nativa (`Error`). No es un `if` por forma del vendor,
 * es una regla sobre el ORIGEN del texto:
 *
 * - Los mensajes que construye pusher-js salen de su propio vocabulario
 *   ('Over capacity', 'App connection limit reached'): son seguros.
 * - El mensaje de una excepción nativa lo construye el runtime a partir de la
 *   ENTRADA. En la forma (4) esa entrada es la trama cruda del websocket, y V8
 *   incrusta un trozo del input en el texto:
 *     `Unexpected token '@', ..."tel 5215512345678"@]" is not valid JSON`
 *   La ventana que V8 recorta va alrededor de la posición del fallo, así que si
 *   la trama se rompe a media conversación, lo que se filtra ES la conversación.
 *
 * Así que de una excepción nativa nos quedamos con su CLASE (`SyntaxError`),
 * que es la señal diagnóstica de verdad —distingue "el proveedor mandó algo que
 * no es JSON" de cualquier otro fallo— y no lleva un solo byte del payload.
 *
 * ## Qué NO hace
 *
 * No decide qué se manda a Sentry ni sanea nada más. El `message` todavía puede
 * traer texto del proveedor, así que el llamador debe dejarlo en `extra` y
 * **nunca** en `tags` ni en el `fingerprint`, para que la capa de redacción de
 * PII lo cubra.
 */

/** Profundidad máxima al desenvolver `.error`. Corta cadenas cíclicas o absurdas. */
const MAX_UNWRAP_DEPTH = 5;

/** Valor reportado cuando de verdad no hay dato. Se conserva el literal previo. */
const UNKNOWN = 'unknown';

const isObject = value => value !== null && typeof value === 'object';

/**
 * `0` y `''` son valores legítimos, así que no sirve `||`. Solo descartamos
 * ausencia real (undefined/null). Un código `0` que se reportara como
 * 'unknown' sería exactamente el bug que estamos arreglando, en pequeño.
 */
const isPresent = value => value !== undefined && value !== null;

/**
 * Presencia para el MENSAJE, donde `''` sí cuenta como ausencia.
 *
 * La razón es concreta: `CloseEvent.reason` viene vacío en la mayoría de los
 * cierres y `getCloseError()` lo copia tal cual a `data.message`. Si `''`
 * contara como presente, la búsqueda se detendría en ese nivel y NO bajaría a
 * un mensaje real que viviera más adentro — y al final ese `''` se convertiría
 * igual en el literal 'Pusher error'. O sea: perderíamos señal para terminar
 * reportando exactamente lo mismo.
 *
 * La asimetría con `isPresent` es deliberada: para el CÓDIGO, `0` es un valor
 * legítimo y descartarlo sería el bug de `||` que este archivo vino a arreglar
 * (ver D2). Un mensaje vacío no es un mensaje; un código `0` sí es un código.
 */
const hasText = value => isPresent(value) && value !== '';

/**
 * ¿Este nivel es una excepción nativa del runtime?
 *
 * `instanceof Error` cubre el caso normal. El segundo brazo cubre el
 * cross-realm (un `Error` nacido en un iframe falla el `instanceof`) mirando la
 * huella de una excepción: `name`, `message` y `stack` como strings. Ninguna de
 * las formas de pusher-js la tiene — sus objetos son `{type, data}` planos — y
 * un `CloseEvent`/`Event` del navegador tampoco trae `.stack`, así que no hay
 * falsos positivos que nos hagan perder el `.reason` del cierre.
 */
const esExcepcionNativa = value =>
  value instanceof Error ||
  (typeof value.stack === 'string' &&
    typeof value.message === 'string' &&
    typeof value.name === 'string');

/**
 * La CLASE de la excepción, que es lo único que copiamos de ella.
 * Corto, estable y sin un byte del payload (ver el docblock de arriba).
 */
const claseDeExcepcion = value => (hasText(value.name) ? value.name : 'Error');

/**
 * Extrae el primer valor presente de `node.data[key]` y luego `node[key]`.
 * pusher-js mete el dato bueno en `data`; la raíz es el fallback para el
 * `CloseEvent` crudo del navegador.
 *
 * `present` se recibe por parámetro porque «presente» no significa lo mismo
 * para el código que para el mensaje (ver `hasText`).
 */
function readField(node, keys, present) {
  const sources = [isObject(node.data) ? node.data : null, node];

  for (const source of sources) {
    if (!source) continue;
    for (const key of keys) {
      if (present(source[key])) return source[key];
    }
  }

  return undefined;
}

/**
 * @param {unknown} err - lo que entrega `connection.bind('error', ...)`.
 * @returns {{ type: string, code: number|string, message: string }}
 *   `type` y `code` son seguros para tags/fingerprint (cortos, no-PII).
 *   `message` puede traer texto del proveedor — tratarlo como dato sensible.
 */
export function normalizePusherError(err) {
  if (!isObject(err)) {
    return { type: UNKNOWN, code: UNKNOWN, message: 'Pusher error' };
  }

  // El `type` que importa es el de fuera: es el que distingue KK ('PusherError')
  // de KV ('WebSocketError') y el que nos dice en qué punto del ciclo falló.
  //
  // Se exige que sea un STRING, no sólo que esté presente: el llamador lo mete
  // en un tag y en el fingerprint vía `String(type)`, y un objeto ahí se
  // convertiría en el tag literal '[object Object]' — un grupo basura en Sentry
  // que además no se puede deshacer. Hoy el vendor siempre manda un string
  // corto; esto es el cinturón para cuando deje de hacerlo.
  //
  // El string VACÍO sigue pasando tal cual, a propósito: es un valor que el
  // vendor podría emitir de verdad y sustituirlo por 'unknown' sería inventar
  // un dato. Lo que se corta aquí es lo que ni siquiera es texto.
  const type = typeof err.type === 'string' ? err.type : UNKNOWN;

  let code;
  let message;
  let node = err;

  for (let depth = 0; isObject(node) && depth < MAX_UNWRAP_DEPTH; depth += 1) {
    // El nivel más externo con dato gana: en `{WebSocketError, error:{...}}`
    // el de fuera no tiene código y el de dentro sí, así que bajamos; pero si
    // el crudo del transporte ya trae `code` en la raíz, nos quedamos ahí.
    if (!isPresent(code)) code = readField(node, ['code'], isPresent);
    if (!hasText(message)) {
      // De una excepción nativa sólo copiamos su clase: su `.message` lo
      // construye el runtime con un trozo de la ENTRADA, y en la forma (4) esa
      // entrada es la trama cruda del websocket. Ver el docblock de arriba.
      message = esExcepcionNativa(node)
        ? claseDeExcepcion(node)
        : readField(node, ['message', 'reason'], hasText);
    }

    if (isPresent(code) && hasText(message)) break;

    node = node.error;
  }

  return {
    type,
    code: isPresent(code) ? code : UNKNOWN,
    // El literal se conserva para no cambiar el título del issue en Sentry
    // cuando de verdad no hay mensaje. `hasText` es el MISMO criterio que usa
    // la búsqueda de arriba: si `''` no detuvo el descenso, tampoco puede
    // colarse aquí como si fuera un mensaje.
    message: hasText(message) ? message : 'Pusher error',
  };
}

export default normalizePusherError;
